export { cn } from "@/utils/cn";

const ONE_MINUTE = 60_000;
const ONE_HOUR = 60 * ONE_MINUTE;
const ONE_DAY = 24 * ONE_HOUR;

export function formatRelativeTime(ms: number): string {
  const diff = Date.now() - ms;
  if (diff < ONE_MINUTE) return "just now";
  if (diff < ONE_HOUR) return `${Math.floor(diff / ONE_MINUTE)}m ago`;
  if (diff < ONE_DAY) return `${Math.floor(diff / ONE_HOUR)}h ago`;
  const d = new Date(ms);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

export function initialsFromName(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return (parts[0] ?? "?").slice(0, 2).toUpperCase();
  return (
    (parts[0]?.[0] ?? "") + (parts[parts.length - 1]?.[0] ?? "")
  ).toUpperCase();
}

// Copy text to the clipboard, resilient to environments where the async
// Clipboard API is unavailable or rejected. The Electron desktop shell
// default-denies every renderer permission request (see apps/desktop main
// process), so `navigator.clipboard.writeText` rejects there; fall back to a
// hidden-textarea `execCommand("copy")`, which is not permission-gated and
// works during the click's user-activation window. Throws only if both paths
// fail, so callers can surface a single error.
export async function copyTextToClipboard(text: string): Promise<void> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return;
    }
  } catch {
    // Fall through to the legacy path below.
  }
  legacyCopy(text);
}

function legacyCopy(text: string): void {
  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  // Keep it out of view and out of the layout/scroll flow.
  textarea.style.position = "fixed";
  textarea.style.top = "0";
  textarea.style.left = "0";
  textarea.style.opacity = "0";
  textarea.style.pointerEvents = "none";
  document.body.appendChild(textarea);
  try {
    textarea.focus();
    textarea.select();
    if (!document.execCommand("copy")) {
      throw new Error("execCommand copy returned false");
    }
  } finally {
    document.body.removeChild(textarea);
  }
}
